/**
 * ═══════════════════════════════════════════════════════════════════════
 * ИМЕНА КЛАССОВ, КОТОРЫХ НЕТ В ЭТАЛОНЕ ОТДЕЛЬНЫМИ УЗЛАМИ.
 *
 * `K` собирается сборщиком из эталона: править его руками нельзя,
 * правка живёт до следующего прогона. Но пара видов у нас есть,
 * а в стенде их не нарисовано — не потому, что мы придумали лишнее,
 * а потому, что стенд их не показывает:
 *
 *   ▸ «ТАКОЙ ГРУППЫ НЕТ» — строка под пустым поиском в выпадении
 *     выбора группы. В стенде она есть в разметке (`grpQueryEmpty`),
 *     но ни одно состояние её не показывает: снимать не с чего.
 *     Числа взяты из самого стенда, не придуманы.
 *
 *   ▸ «ТОЛЬКО ПРОСТОРНО» — граница 1120, на которой эталон убирает
 *     выбор кабинета из шапки. Это НЕ отдельный вид узла, а условие
 *     показа: в эталоне оно записано в коде стенда (`roomy`), а не
 *     стилем, и вырезать его сборщику неоткуда.
 *
 *   ▸ «НЕ НА ТЕЛЕФОНЕ» — то же самое для названия раздела в шапке
 *     (`titleOn: !phone`). Оба условия — про шапку и про место в ней.
 *
 * Правила — рядом, в `dobavki.css`, и там же довод. Имена здесь,
 * чтобы разметка не писала строковых литералов: опечатка в классе
 * не ломает сборку и не видна ни одной проверке.
 * ═══════════════════════════════════════════════════════════════════════
 */
export const D = {
  /* Призрачное поле ставки в строке запроса — ТЗ 02.09.2026, раздел 4.
     Довод, откуда взяты числа, — рядом в `dobavki.css`. */
  полеСтавкиПризрак: 'k-поле-ставки-призрак',
  полеСтавкиПризракТест: 'k-поле-ставки-призрак-тест',
  /* Товарная ставка, показанная в строке запроса за неимением своей.
     Довод — в шапке `clusters/bid-cell.tsx`. */
  полеСтавкиТоварная: 'k-поле-ставки-товарная',
  /* Наведение у кнопки «Вернуть как было» в меню колонок. */
  вернутьКакБыло: 'k-вернуть-как-было',
  /* Шапка таблицы запросов по эталону 02.09 — довод в `dobavki.css`. */
  шапкаЗапросов0209: 'k-шапка-запросов-02-09',
  /* Потолок ширины у поля поиска запросов — довод в `dobavki.css`. */
  поискЗапросовПотолок: 'k-поиск-запросов-потолок',
  нетТакойГруппы: 'k-нет-такой-группы',
  толькоПросторно: 'k-только-просторно',
  неНаТелефоне: 'k-не-на-телефоне',
  /* Два вида плиток показателей: на широком — сетка карточек,
     на телефоне — одна карточка с сеткой 2×2 и кнопкой «Ещё». */
  настольныеПлитки: 'k-настольные-плитки',
  телефонныеПлитки: 'k-телефонные-плитки',
  /* ═══════════════════════════════════════════════════════════════════
     ЗАПРОСЫ НА ТЕЛЕФОНЕ — КАРТОЧКИ ВМЕСТО ТАБЛИЦЫ (ответ К-131).

     У эталона это ровно так и сделано: коробка прокрутки таблицы
     получает `display: none`, а над ней рисуется список карточек.
     Оба узла названы (`прокрутка-запросов`, `список-запросов-390`),
     и сверка ходит по именам — значит, оба обязаны быть в разметке
     на обеих ширинах, различаясь только показом.

     Прятать, а не выбрасывать, ещё и дешевле на повороте планшета
     через границу 1024: таблица не пересобирается, поперечная
     прокрутка остаётся там, где человек её оставил.
     ═══════════════════════════════════════════════════════════════ */
  толькоТаблицаЗапросов: 'k-только-таблица-запросов',
  толькоКарточкиЗапросов: 'k-только-карточки-запросов',
  /* ═══════════════════════════════════════════════════════════════════
     ПЛАШКИ НА ЭКРАНАХ ДО ВХОДА — ЧИСЛА ИЗ ЗАМЕРА, НЕ ИЗ ГОЛОВЫ.

     Состояний `вход · отказ` и `вход · восстановление` стенд
     не ОТРИСОВЫВАЕТ: они есть в замере, но снять с них разметку нечем,
     и сборщику частиц резать неоткуда.

     Что известно точно (`design/measured/Bidra-Kabinet.json`):
     `отказ-входа` на 1440 — 330 × 80 при карточке 380 с полями
     `--sp-6`, то есть во всю ширину содержимого. Цвета взяты
     из семейства плашек эталона: заливка `--bad-wash`, чернила
     `--bad-ink` — те же, что у «в чёрном списке».

     Заведи дизайнер отрисовку этих состояний — частицы вырежутся
     сборщиком и эти две строки уйдут.
     ═══════════════════════════════════════════════════════════════ */
  отказВхода: 'k-отказ-входа',
  заметкаВхода: 'k-заметка-входа',
  /* ═══════════════════════════════════════════════════════════════════
     РАЗДЕЛ «КАБИНЕТЫ» — СВОЯ СЕТКА И СВОЙ РЯД ДЕЙСТВИЙ.

     Эталон рисует кабинеты один раз — на экране ВЫБОРА, где карточка
     только называет кабинет. Раздела, в котором над кабинетом ещё
     и действуют, в эталоне нет вовсе: `Bidra-Kabinet--Кабинеты`
     это тот же выбор («Выберите кабинет»), а не настройки.

     Поэтому сетка и ряд действий — здесь, а не в `K`: сборщику
     вырезать их неоткуда, и выдумывать за дизайнера новую карточку
     мы не стали. Карточка та же, эталонная; шире только столбец,
     потому что под целями встал ряд кнопок. Числа — из замера
     в браузере, довод целиком в `dobavki.css`.
     ═══════════════════════════════════════════════════════════════ */
  сеткаУправленияКабинетами: 'k-сетка-управления-кабинетами',
  /* Волосяная чёрточка между частями подстрочника шапки кампании.
     В эталоне она нарисована инлайном внутри самой строки и потому
     сборщику не достаётся отдельным узлом; довод — в `dobavki.css`. */
  чертаПодстрочника: 'k-черта-подстрочника',
  /* Приглушённое справочное число в строке товара — довод там же. */
  справкаЧисла: 'rk-spravka',
  /* Крестик очистки в поле поиска запросов — довод в `dobavki.css`. */
  очиститьПоиск: 'k-очистить-поиск',
  /* Галочка «поставить эту ставку» в таблице замеров теста: прячется
     до наведения там, где есть мышь. Довод — в `dobavki.css`. */
  галочкаПрименения: 'k-галочка-применения',
  галочкаПримененияОткрыта: 'k-галочка-применения-открыта',
  /* ═══════════════════════════════════════════════════════════════════
     НАВЕДЕНИЯ ОКНА ТЕСТА СТАВКИ — ВСЕ СЕМЬ, ИЗ `style-hover` ЭТАЛОНА.

     10.09.2026 владелец увидел одно из них («при наведении кнопочка
     не становится красной») — а не было ни одного: стенд идущего теста
     не рисует ни в одном состоянии, сборщик частиц вырезать оттуда
     ничего не может, и при переносе в кабинет наведения потерялись все
     сразу. Инлайновым стилем они не задаются вовсе.

     Правила и доводы — рядом, в `dobavki.css`; значения там сверяются
     с эталоном сторожем `idyot-test-kak-istoriya.spec.ts`.
     ═══════════════════════════════════════════════════════════════ */
  кнопкаОтменитьТест: 'k-кнопка-отменить-тест',
  кнопкаЗапуститьЗаново: 'k-кнопка-запустить-заново',
  шапкаПрошлогоТеста: 'k-шапка-прошлого-теста',
  кнопкаПрименитьКластеру: 'k-кнопка-применить-к-кластеру',
  кнопкаПрименитьКоВсей: 'k-кнопка-применить-ко-всей',
  кнопкаЗакрытьПрименение: 'k-кнопка-закрыть-применение',
  подписьДействийКабинета: 'k-подпись-действий-кабинета',
  кнопкиДействийКабинета: 'k-кнопки-действий-кабинета',
  /* Ширина выпадения шаблонов расписания: 300 на просторе, 82vw
     на телефоне. Правило — в `dobavki.css`, там же довод, почему
     оно не может быть вписанным стилем. */
  менюШаблонов: 'k-меню-шаблонов',
} as const;
